import { useState, useEffect, useRef } from 'react';
import api from '../services/api';

// Make a layer title safe for a shapefile filename
// Web Mercator (Auxiliary Sphere) — the data's native CRS and what the CAD team's
// Civil 3D drawings expect. Civil 3D reprojects this into the drawing's State Plane
// (feet) on import. Must match their known-good export's .prj byte-for-byte.
const WEB_MERCATOR_WKID = 102100;
const WEB_MERCATOR_PRJ = 'PROJCS["WGS_1984_Web_Mercator_Auxiliary_Sphere",GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137.0,298.257223563]],PRIMEM["Greenwich",0.0],UNIT["Degree",0.017453292519943295]],PROJECTION["Mercator_Auxiliary_Sphere"],PARAMETER["False_Easting",0.0],PARAMETER["False_Northing",0.0],PARAMETER["Central_Meridian",0.0],PARAMETER["Standard_Parallel_1",0.0],PARAMETER["Auxiliary_Sphere_Type",0.0],UNIT["Meter",1.0]]';

function sanitizeName(name) {
  return (String(name).replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 60)) || 'layer';
}

// GeoJSON geometry type -> shapefile shape type
function shpTypeOf(t) {
  if (t === 'Point' || t === 'MultiPoint') return 'POINT';
  if (t === 'LineString' || t === 'MultiLineString') return 'POLYLINE';
  if (t === 'Polygon' || t === 'MultiPolygon') return 'POLYGON';
  return null;
}

// Shape one feature's coordinates so shp-write's low-level writer emits exactly ONE
// record per feature. A bare LineString must be wrapped as a single-part polyline;
// everything else already matches the writer's expected nesting.
function geomElement(geom) {
  if (geom.type === 'LineString') return [geom.coordinates];
  return geom.coordinates;
}

// DBF field names are capped at 10 chars and must be unique. ArcGIS field names
// (e.g. ESRIGNSS_AVG_H_RMS / ESRIGNSS_AVG_V_RMS) collide once truncated, so map
// each original attribute key to a unique, sanitized, ≤10-char field name.
function buildFieldMap(keys) {
  const map = {};
  const used = new Set();
  for (const k of keys) {
    const base = (String(k).replace(/[^A-Za-z0-9_]/g, '_').slice(0, 10)) || 'FIELD';
    let name = base;
    let n = 1;
    while (used.has(name.toLowerCase())) {
      const suffix = String(n);
      name = base.slice(0, 10 - suffix.length) + suffix;
      n += 1;
    }
    used.add(name.toLowerCase());
    map[k] = name;
  }
  return map;
}

export default function ViewMapModal({ job, onClose }) {
  const [phase, setPhase] = useState('loading'); // loading | picking | map | error
  const [status, setStatus] = useState('Starting map…');
  const [error, setError] = useState('');
  const [webmaps, setWebmaps] = useState([]);
  const [search, setSearch] = useState('');
  const [hasRect, setHasRect] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Layers that intersect the drawn frame: [{ id, title, count, included }] — null until a rect
  // is drawn, 'counting' while the per-layer counts are being queried.
  const [frameLayers, setFrameLayers] = useState(null);

  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const webmapRef = useRef(null);
  const opLayersRef = useRef([]);     // the web map's operational feature layers
  const graphicsRef = useRef(null);
  const sketchRef = useRef(null);
  const rectRef = useRef(null);

  useEffect(() => {
    init();
    return () => { try { viewRef.current?.destroy?.(); } catch {} };
  }, []);

  // Search the org's web maps server-side as the user types (debounced)
  useEffect(() => {
    if (phase !== 'picking') return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/arcgis/webmaps', { params: { q: search } });
        setWebmaps(data);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [search, phase]);

  async function init() {
    try {
      const { data } = await api.get('/arcgis/clientmap', { params: { client: job.client_name } });
      if (data.webmap_item_id) {
        loadMap(data.webmap_item_id);
      } else {
        const { data: maps } = await api.get('/arcgis/webmaps');
        setWebmaps(maps);
        setPhase('picking');
      }
    } catch {
      setError('Could not start the map. Check ArcGIS access.');
      setPhase('error');
    }
  }

  async function chooseMap(webmapId) {
    try {
      await api.post('/arcgis/clientmap', { client_name: job.client_name, webmap_item_id: webmapId });
    } catch { /* non-fatal */ }
    loadMap(webmapId);
  }

  async function loadMap(webmapId) {
    setPhase('map');
    setStatus('Loading map…');
    try {
      const { data: tok } = await api.get('/arcgis/portal-token');

      const [
        { default: esriConfig },
        { default: esriId },
        { default: WebMap },
        { default: MapView },
        { default: FeatureLayer },
        { default: GraphicsLayer },
        { default: SketchViewModel },
      ] = await Promise.all([
        import('@arcgis/core/config'),
        import('@arcgis/core/identity/IdentityManager'),
        import('@arcgis/core/WebMap'),
        import('@arcgis/core/views/MapView'),
        import('@arcgis/core/layers/FeatureLayer'),
        import('@arcgis/core/layers/GraphicsLayer'),
        import('@arcgis/core/widgets/Sketch/SketchViewModel'),
      ]);

      esriConfig.portalUrl = tok.orgUrl;
      esriId.registerToken({ server: `${tok.orgUrl}/sharing/rest`, token: tok.token, ssl: true });

      const webmap = new WebMap({ portalItem: { id: webmapId } });
      const view = new MapView({ container: mapRef.current, map: webmap });
      viewRef.current = view;
      webmapRef.current = webmap;
      await view.when();
      await webmap.loadAll().catch(() => {});

      // Snapshot ALL operational feature layers, including ones nested inside group layers
      opLayersRef.current = webmap.allLayers.filter((l) => l.type === 'feature').toArray();

      // Drawing layer + polygon/rectangle tool
      const gl = new GraphicsLayer();
      webmap.add(gl);
      graphicsRef.current = gl;
      const svm = new SketchViewModel({ view, layer: gl });
      svm.on('create', (e) => {
        if (e.state === 'complete') {
          rectRef.current = e.graphic.geometry;
          setHasRect(true);
          analyzeFrame(e.graphic.geometry);
        }
      });
      sketchRef.current = svm;

      // Find the project's feature layer and zoom to it (added after the snapshot so it's not exported)
      setStatus('Finding the job…');
      try {
        const { data: layer } = await api.get(`/arcgis/project-layer/${encodeURIComponent(job.project_id)}`);
        const svcRoot = layer.url.split('/rest/')[0] + '/rest/services';
        esriId.registerToken({ server: svcRoot, token: tok.token, ssl: true });
        const fl = new FeatureLayer({ url: `${layer.url}/0`, outFields: ['*'] });
        webmap.add(fl);
        await fl.when();
        const res = await fl.queryExtent();
        if (res?.extent) await view.goTo(res.extent.expand(1.6));
        setStatus('');
      } catch {
        setStatus(`No feature layer found for ${job.project_id}.`);
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load the map.');
      setPhase('error');
    }
  }

  // Tear down the current map and re-open the picker so a new map can be linked
  async function changeMap() {
    try { viewRef.current?.destroy?.(); } catch {}
    viewRef.current = null;
    webmapRef.current = null;
    opLayersRef.current = [];
    graphicsRef.current = null;
    sketchRef.current = null;
    rectRef.current = null;
    setHasRect(false);
    setFrameLayers(null);
    setStatus('');
    setSearch('');
    setPhase('loading');
    try {
      const { data: maps } = await api.get('/arcgis/webmaps');
      setWebmaps(maps);
      setPhase('picking');
    } catch {
      setError('Could not load the map list.');
      setPhase('error');
    }
  }

  function startDraw(tool) {
    if (!sketchRef.current) return;
    graphicsRef.current.removeAll();
    rectRef.current = null;
    setHasRect(false);
    setFrameLayers(null);
    sketchRef.current.create(tool);
  }

  // Count each operational layer's features inside the frame so the panel only
  // lists layers that would actually contribute to the export. All start included.
  async function analyzeFrame(rect) {
    setFrameLayers('counting');
    const counts = await Promise.all(
      opLayersRef.current.map(async (layer) => {
        try {
          const q = layer.createQuery();
          q.geometry = rect;
          q.spatialRelationship = 'intersects';
          const count = await layer.queryFeatureCount(q);
          return { id: layer.id, title: layer.title || `layer_${layer.id}`, count, included: true };
        } catch {
          return null; // count failed — leave it out of the panel; export still tries it
        }
      })
    );
    // Only show this rect's result if the user hasn't drawn a newer rect meanwhile
    if (rectRef.current === rect) {
      setFrameLayers(counts.filter((l) => l && l.count > 0));
    }
  }

  function toggleLayer(id) {
    setFrameLayers((prev) =>
      Array.isArray(prev) ? prev.map((l) => (l.id === id ? { ...l, included: !l.included } : l)) : prev
    );
  }

  async function doExport() {
    if (!rectRef.current) return;
    setExporting(true);
    try {
      const [tmod, smod, jszipMod] = await Promise.all([
        import('@terraformer/arcgis'),
        import('@mapbox/shp-write'),
        import('jszip'),
      ]);
      const arcgisToGeoJSON = tmod.arcgisToGeoJSON || tmod.default?.arcgisToGeoJSON;
      const writeShp = smod.write || smod.default?.write;
      const JSZip = jszipMod.default || jszipMod;

      // Build a shapefile (shp/shx/dbf/prj) for one geometry-type group via the
      // low-level writer — one record per feature, so .dbf and .shx stay in sync.
      const buildShp = (shpType, features) => {
        const keys = [...new Set(features.flatMap((f) => Object.keys(f.properties || {})))];
        const fieldMap = buildFieldMap(keys);
        const rows = features.map((f) => {
          const props = f.properties || {};
          const out = {};
          for (const k of Object.keys(props)) out[fieldMap[k]] = props[k];
          return out;
        });
        const geometries = features.map((f) => geomElement(f.geometry));
        let out = null;
        writeShp(rows, shpType, geometries, (err, files) => { if (!err) out = files; });
        return out;
      };

      const rect = rectRef.current;

      // Skip layers the user unchecked in the panel; layers that never made it into
      // the panel (count query failed) are still attempted, matching prior behavior.
      const excluded = new Set(
        Array.isArray(frameLayers) ? frameLayers.filter((l) => !l.included).map((l) => l.id) : []
      );
      const exportLayers = opLayersRef.current.filter((l) => !excluded.has(l.id));

      // Query each layer separately so we can keep each as its own shapefile
      const layerResults = await Promise.all(
        exportLayers.map(async (layer) => {
          try {
            const q = layer.createQuery();
            q.geometry = rect;
            q.spatialRelationship = 'intersects';
            q.returnGeometry = true;
            q.outFields = ['*'];
            q.outSpatialReference = { wkid: WEB_MERCATOR_WKID }; // Web Mercator (meters), reprojected server-side
            const fs = await layer.queryFeatures(q);
            const features = fs.features
              .filter((f) => f.geometry)
              .map((f) => ({ type: 'Feature', properties: f.attributes || {}, geometry: arcgisToGeoJSON(f.geometry.toJSON()) }));
            return { name: layer.title || `layer_${layer.id}`, features };
          } catch (err) {
            console.warn('Layer query failed:', layer.title, err);
            return { name: layer.title, features: [] };
          }
        })
      );

      const withData = layerResults.filter((l) => l.features.length);
      if (!withData.length) {
        alert('No features found inside that area.');
        return;
      }

      // Build one shapefile per source layer, merged into a single zip
      const master = new JSZip();
      const used = {};
      for (const l of withData) {
        let safe = sanitizeName(l.name);
        if (used[safe]) { used[safe] += 1; safe = `${safe}_${used[safe]}`; } else { used[safe] = 1; }

        // Split the layer's features by shapefile geometry type (a shapefile holds one type)
        const groups = { POINT: [], POLYLINE: [], POLYGON: [] };
        for (const f of l.features) {
          const t = shpTypeOf(f.geometry?.type);
          if (t) groups[t].push(f);
        }
        const present = Object.keys(groups).filter((t) => groups[t].length);
        for (const t of present) {
          const files = buildShp(t, groups[t]);
          if (!files) continue;
          const base = present.length > 1 ? `${safe}_${t}` : safe;
          master.file(`${base}.shp`, files.shp.buffer, { binary: true });
          master.file(`${base}.shx`, files.shx.buffer, { binary: true });
          master.file(`${base}.dbf`, files.dbf.buffer, { binary: true });
          master.file(`${base}.prj`, WEB_MERCATOR_PRJ);
        }
      }

      const blob = await master.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job.project_id}_export.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert('Export failed. See console for details.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-5xl rounded-xl border border-navy-500 overflow-hidden flex flex-col" style={{ backgroundColor: '#0d1b2e', height: '85vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: '#1e3a5f', backgroundColor: '#080f1a' }}>
          <div>
            <h2 className="text-white font-bold text-sm tracking-wide">🗺️ {job.project_id}</h2>
            <p className="text-gray-400 text-xs">{job.client_name}{status && ` · ${status}`}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition text-2xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="relative flex-1">
          <div ref={mapRef} className="absolute inset-0" />

          {/* Export toolbar */}
          {phase === 'map' && (
            <div className="absolute top-3 left-3 z-10 flex gap-2">
              <button
                onClick={() => startDraw('polygon')}
                disabled={exporting}
                className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
                style={{ backgroundColor: '#0d1b2e', color: '#c9a84c', border: '1px solid #c9a84c' }}
              >
                ⬠ Draw Polygon
              </button>
              <button
                onClick={() => startDraw('rectangle')}
                disabled={exporting}
                className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
                style={{ backgroundColor: '#0d1b2e', color: '#9ca3af', border: '1px solid #1e3a5f' }}
              >
                ▭ Rectangle
              </button>
              <button
                onClick={doExport}
                disabled={!hasRect || exporting || (Array.isArray(frameLayers) && frameLayers.length > 0 && frameLayers.every((l) => !l.included))}
                className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
                style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
              >
                {exporting ? 'Exporting…' : '⤓ Export Shapefile'}
              </button>
              <button
                onClick={changeMap}
                disabled={exporting}
                title={`Change which map is linked to ${job.client_name}`}
                className="px-3 py-2 rounded text-xs font-bold uppercase tracking-wide shadow-lg disabled:opacity-40"
                style={{ backgroundColor: '#0d1b2e', color: '#9ca3af', border: '1px solid #1e3a5f' }}
              >
                ⇄ Change Map
              </button>
            </div>
          )}

          {/* Layers-in-frame panel — appears once an export area is drawn */}
          {phase === 'map' && frameLayers !== null && (
            <div
              className="absolute top-3 right-3 z-10 rounded shadow-lg text-xs"
              style={{ backgroundColor: '#0d1b2e', border: '1px solid #1e3a5f', width: '15rem', maxHeight: '60%', overflowY: 'auto' }}
            >
              <p className="px-3 py-2 font-bold uppercase tracking-wide border-b" style={{ color: '#c9a84c', borderColor: '#1e3a5f' }}>
                Layers in Frame
              </p>
              {frameLayers === 'counting' ? (
                <p className="px-3 py-2 text-gray-400 italic">Checking layers…</p>
              ) : frameLayers.length === 0 ? (
                <p className="px-3 py-2 text-gray-400 italic">No layers have features in this area.</p>
              ) : (
                <div className="py-1">
                  {frameLayers.map((l) => (
                    <label key={l.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-navy-700 text-gray-200">
                      <input
                        type="checkbox"
                        checked={l.included}
                        onChange={() => toggleLayer(l.id)}
                        disabled={exporting}
                        className="accent-gold-500"
                      />
                      <span className={`flex-1 truncate ${l.included ? '' : 'line-through text-gray-500'}`} title={l.title}>
                        {l.title}
                      </span>
                      <span className="text-gray-500">{l.count}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {phase === 'loading' && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: '#0d1b2e' }}>
              <p className="text-gray-400 text-sm">Starting…</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: '#0d1b2e' }}>
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {phase === 'picking' && (
            <div className="absolute inset-0 flex items-center justify-center p-6" style={{ backgroundColor: '#0d1b2e' }}>
              <div className="w-full max-w-md">
                <p className="text-white font-bold text-sm mb-1">Which map is for {job.client_name}?</p>
                <p className="text-gray-400 text-xs mb-3">Pick it once — future jobs for this client will open straight to it.</p>
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search all maps… (e.g. millman)"
                  className="w-full mb-3 rounded px-3 py-2 text-sm text-white border focus:outline-none"
                  style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
                />
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {webmaps.length === 0 ? (
                    <p className="text-gray-500 text-xs italic">No maps match.</p>
                  ) : webmaps.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => chooseMap(m.id)}
                      className="w-full text-left px-3 py-2 rounded border text-sm text-gray-200 transition hover:border-gold-500"
                      style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
                    >
                      {m.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
